<template>
  <div class="image-management-page">
    <div class="page-header">
      <h1 class="page-title">{{ $t('imageManagement.swiperImages') }}</h1>
      <div class="header-actions">
        <el-button type="primary" class="upload-btn" @click="openUploadDialog">
          <el-icon><Upload /></el-icon>
          {{ $t('imageManagement.uploadImage') }}
        </el-button>
        <el-button class="refresh-btn" @click="loadImages">
          <el-icon><Refresh /></el-icon>
          {{ $t('imageManagement.refresh') }}
        </el-button>
      </div>
    </div>

    <div class="images-grid" v-loading="loading">
      <div v-if="images.length === 0" class="empty-state">
        <el-empty :description="$t('imageManagement.noImages')" />
      </div>

      <div v-else class="image-cards">
        <div v-for="image in images" :key="image.id" class="image-card">
          <div class="image-wrapper">
            <ImagePreview 
              :src="image.url" 
              width="100%" 
              height="200px"
              :enable-preview="true"
            />
          </div>
          
          <div class="image-info">
            <div class="image-name">{{ image.displayName || image.fileName }}</div>
            <div class="image-actions">
              <el-button 
                type="danger" 
                size="small" 
                @click="deleteImageItem(image)"
              >
                Delete
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="showDialog"
      :title="$t('imageManagement.uploadImage')"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="upload-content">
        <el-upload
          v-if="!previewImage"
          class="image-uploader"
          drag
          :show-file-list="false"
          :auto-upload="false"
          :on-change="handleFileSelect"
          accept="image/*"
        >
          <div class="upload-placeholder">
            <el-icon><Upload /></el-icon>
            <div class="upload-text">Click or drag file to upload</div>
            <div class="upload-hint">PNG, JPG, GIF, WEBP up to 10MB</div>
          </div>
        </el-upload>

        <div v-else class="preview-wrapper">
          <ImagePreview 
            :src="previewImage" 
            width="100%" 
            height="300px"
            fit="contain"
            :enable-preview="false"
          />
          <el-button 
            class="remove-image-btn" 
            type="danger" 
            size="small" 
            @click="() => { previewImage = ''; selectedFile = null; }"
          >
            Remove Image
          </el-button>
        </div>

        <el-form v-if="previewImage" class="upload-form" @submit.prevent="confirmUpload">
          <el-form-item :label="$t('imageManagement.displayName')">
            <el-input 
              v-model="uploadForm.displayName" 
              :placeholder="$t('imageManagement.displayNamePlaceholder')"
            />
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="cancelUpload">Cancel</el-button>
          <el-button type="primary" @click="confirmUpload" :loading="uploading">
            Confirm Upload
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Upload, Refresh } from '@element-plus/icons-vue'
import ImagePreview from '@/components/ImagePreview.vue'
import { getSwiperImages, uploadSwiperImage, deleteSwiperImage } from '@/api/image'
import { useUserStore } from '@/stores/user'
import { compressImage } from '@/utils/image-compressor'

const userStore = useUserStore()

const loading = ref(false)
const uploading = ref(false)
const showDialog = ref(false)
const images = ref([])
const previewImage = ref('')
const selectedFile = ref(null)

const uploadForm = reactive({
  displayName: '',
  displayOrder: 0
})

const loadImages = async () => {
  loading.value = true
  try {
    const tenantId = userStore.userInfo?.tenantId || 1
    const res = await getSwiperImages(tenantId)
    
    if (res.success && res.data) {
      images.value = res.data.map((item, index) => ({
        id: item.id || index + 1,
        fileName: item.fileName,
        displayName: item.displayName,
        url: item.url,
        storagePath: item.storagePath,
        displayOrder: item.displayOrder,
        createdAt: item.createdAt
      }))
    } else {
      images.value = []
    }
  } catch (error) {
    console.error('Failed to load images:', error)
    ElMessage.error('Failed to load images')
  } finally {
    loading.value = false
  }
}

const handleFileSelect = (event) => {
  const file = event.raw || event.target?.files?.[0]
  if (!file) return

  if (!file.type.startsWith('image/')) {
    ElMessage.error('Please select an image file')
    return
  }

  if (file.size > 10 * 1024 * 1024) {
    ElMessage.error('File size must be less than 10MB')
    return
  }

  selectedFile.value = file
  uploadForm.displayName = file.name.replace(/\.[^/.]+$/, '')

  const reader = new FileReader()
  reader.onload = (e) => {
    previewImage.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const openUploadDialog = () => {
  uploadForm.displayName = ''
  uploadForm.displayOrder = 0
  previewImage.value = ''
  selectedFile.value = null
  showDialog.value = true
}

const cancelUpload = () => {
  showDialog.value = false
}

const confirmUpload = async () => {
  if (!selectedFile.value) {
    ElMessage.error('Please select an image')
    return
  }

  uploading.value = true
  try {
    const tenantId = userStore.userInfo?.tenantId || 1
    
    // Compress image if larger than 170KB
    let fileToUpload = selectedFile.value
    if (selectedFile.value.size > 170 * 1024) {
      ElMessage.info('Compressing image...')
      fileToUpload = await compressImage(selectedFile.value, 0.17, 1920)
    }
    
    const res = await uploadSwiperImage(tenantId, {
      file: fileToUpload,
      displayName: uploadForm.displayName,
      displayOrder: uploadForm.displayOrder
    })
    
    if (res.success) {
      ElMessage.success('Image uploaded successfully')
      showDialog.value = false
      loadImages()
    } else {
      ElMessage.error(res.message || 'Failed to upload image')
    }
  } catch (error) {
    console.error('Failed to upload image:', error)
    ElMessage.error('Failed to upload image')
  } finally {
    uploading.value = false
  }
}

const deleteImageItem = async (image) => {
  try {
    await ElMessageBox.confirm(
      `Are you sure you want to delete "${image.displayName || image.fileName}"?`,
      'Confirm Delete',
      {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
    )

    const tenantId = userStore.userInfo?.tenantId || 1
    const res = await deleteSwiperImage(tenantId, image.id)
    
    if (res.success) {
      ElMessage.success('Image deleted successfully')
      loadImages()
    } else {
      ElMessage.error(res.message || 'Failed to delete image')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Failed to delete image:', error)
      ElMessage.error('Failed to delete image')
    }
  }
}

onMounted(() => {
  loadImages()
})
</script>

<style scoped lang="scss">
.image-management-page {
  height: 100%;
  background-color: #f5f7fa;
  padding: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  .page-title {
    font-size: 20px;
    font-weight: 600;
    color: #303133;
    margin: 0;
  }

  .header-actions {
    display: flex;
    gap: 12px;

    .upload-btn {
      background-color: #409eff;
      border-color: #409eff;
    }
  }
}

.images-grid {
  flex: 1;
  overflow-y: auto;
}

.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.image-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}

.image-card {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .image-wrapper {
    width: 100%;
    height: 200px;
    background-color: #f5f7fa;
  }

  .image-info {
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .image-name {
      font-size: 14px;
      color: #303133;
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      margin-right: 8px;
    }

    .image-actions {
      flex-shrink: 0;
    }
  }
}

.upload-content {
  .image-uploader {
    width: 100%;

    :deep(.el-upload-dragger) {
      width: 100%;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .el-icon {
      font-size: 48px;
      color: #909399;
    }

    .upload-text {
      font-size: 14px;
      color: #606266;
    }

    .upload-hint {
      font-size: 12px;
      color: #909399;
    }
  }

  .preview-wrapper {
    margin-bottom: 20px;

    .remove-image-btn {
      margin-top: 12px;
      width: 100%;
    }
  }

  .upload-form {
    margin-top: 16px;
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
